import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Link } from "wouter";
import { SearchStateLink, type SearchState } from "@/components/search/SearchStateLink";
import MustangLocationSearch from "./MustangLocationSearch";
import { MustangCard } from "./MustangLiveLanding";
import type { CarCardData } from "@/lib/api";
import { locationCars, guideCityInventoryPaths, type MarketingLocation } from "@/lib/marketing-location-stock.mjs";
import { useDailyLandingInventory } from "@/hooks/useDailyLandingInventory";
import "./MustangSourceLanding.css";
import type { MarketingArt, MarketingBlock, MarketingReview, MarketingSection } from "@/lib/marketing-source-types.mjs";
import { useRuntimeBrandContent } from "@/hooks/useRuntimeBrandContent";
export type { MarketingArt, MarketingBlock, MarketingReview, MarketingSection, MarketingSectionArt } from "@/lib/marketing-source-types.mjs";

export interface MarketingSourceLandingProps {
  /** Optional slots (additive): used by curated hire landings. */
  heroTop?: ReactNode;
  heroAction?: ReactNode;
  inventoryControls?: ReactNode;
  inventoryNotice?: ReactNode;
  inventoryFooter?: ReactNode;
  renderCard?: (car: CarCardData, index: number) => ReactNode;
  retryLabel?: string;
  unavailableLabel?: string;
  emptyLabel?: string;
  browseLabel?: string;
  faqHeadingLabel?: string;
  relatedHeadingLabel?: string;
  heroHostCta?: boolean;
  hostCta?: boolean;
  /** Exact source section order; when present replaces intro/after layout. */
  sections?: MarketingSection[];
  /** Real DB-backed renter reviews, already filtered to the page market. */
  reviews?: MarketingReview[];
  heading: string;
  subtitle: string;
  lang?: string;
  hero: { src: string; srcSet?: string; alt: string; width?: number; height?: number; objectPosition?: string };
  introBlocks: MarketingBlock[] | null;
  introLinks?: { href: string; label: string }[];
  introArt?: MarketingArt;
  photoBand?: { url: string; blocks: MarketingBlock[] | null };
  inventoryHeading: string;
  afterBlocks?: MarketingBlock[] | null;
  afterLinks?: { href: string; label: string }[];
  afterArt?: MarketingArt;
  searchState: SearchState;
  cars: CarCardData[];
  loading: boolean;
  unavailable: boolean;
  fetching: boolean;
  onRetry: () => void;
  dataAttrs?: Record<string, string | undefined>;
  seoSupplement: {
    faqs: { q: string; a: string }[];
    internalLinks: { href: string; label: string; section: string }[];
  };
}

type ShelfState = { loading: boolean; unavailable: boolean; fetching: boolean; onRetry: () => void };
function GuideCityShelf({ section, render }: {
  section: Extract<MarketingSection, { kind: "inventory" }>;
  render: (heading: string, cars: CarCardData[], href?: string, state?: ShelfState) => ReactNode;
}) {
  const path = guideCityInventoryPaths[section.location!.city];
  const stock = useDailyLandingInventory({ path, country_code: "US", brand: "driveshare", locale: "en" });
  return <>{render(section.heading, locationCars(stock.cars, section.location), section.searchHref, {
    loading: stock.isLoading, fetching: stock.isFetching,
    unavailable: stock.scopeInvalid || (stock.isError && !stock.inventory) || stock.inventory?.state === "unavailable",
    onRetry: () => { void stock.refetch(); },
  })}</>;
}

function Blocks({ blocks, prefix }: { blocks: MarketingBlock[] | null | undefined; prefix: string }) {
  return (
    <>
      {blocks?.map((b, i) => {
        const key = `${prefix}-${i}`;
        if (b.type === "h2") return <h2 key={key}>{b.text}</h2>;
        if (b.type === "p") return <p key={key}>{b.text}</p>;
        if (b.type === "ul") return <ul key={key}>{b.items.map((x, j) => <li key={j}>{x}</li>)}</ul>;
        if (b.type === "ol") return <ol key={key}>{b.items.map((x, j) => <li key={j}>{x}</li>)}</ol>;
        if (b.type === "html") return <div key={key} dangerouslySetInnerHTML={{ __html: b.html }} />;
        if (b.type !== "image") return null;
        return <img key={key} src={b.url} alt={b.alt || ""} loading="lazy" decoding="async" style={{ width: "100%", height: "auto", margin: "16px 0" }} />;
      })}
    </>
  );
}

function Links({ links, label }: { links?: { href: string; label: string }[]; label: string }) {
  if (!links?.length) return null;
  return (
    <nav aria-label={label} className="mlx-copy__links">
      {links.map((l) => <Link key={l.href} href={l.href}>{l.label}</Link>)}
    </nav>
  );
}

function Art({ art, sizes }: { art?: MarketingArt & { width?: number; height?: number; srcSet?: string }; sizes: string }) {
  if (!art) return null;
  const cls = art.aspectRatio === "portrait" ? "mlx-art mlx-art--portrait" : art.aspectRatio === "square" ? "mlx-art mlx-art--square" : "mlx-art";
  const srcSet = art.srcSet || (art.variants?.length ? art.variants.map((v) => `${v.url} ${v.width}w`).join(", ") : undefined);
  return (
    <figure className={cls} style={{ margin: "32px auto" }}>
      <img src={art.url} srcSet={srcSet} width={art.width} height={art.height} sizes={srcSet ? sizes : undefined} alt={art.alt} loading="lazy" decoding="async" />
    </figure>
  );
}

export default function MarketingSourceLanding(input: MarketingSourceLandingProps) {
  const p = useRuntimeBrandContent(input);
  const { t } = useTranslation();
  const track = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => {
    const el = track.current;
    const card = el?.querySelector("article");
    if (el && card) el.scrollBy({ left: dir * (card.getBoundingClientRect().width + 32), behavior: "smooth" });
  };
  const introEl = (<>
      {p.introBlocks && (
        <section className="mlx-copy">
          <div className="mlx-copy__in">
            <Blocks blocks={p.introBlocks} prefix="intro" />
            <Links links={p.introLinks} label={t("footer.how_to_rent")} />
          </div>
          <Art art={p.introArt} sizes="(min-width:768px) 704px, 100vw" />
        </section>
      )}

      {p.photoBand && (
        <section className="mlx-band">
          <img src={p.photoBand.url} alt="" loading="lazy" decoding="async" />
          <div className="mlx-copy mlx-copy--flat"><div className="mlx-copy__in"><Blocks blocks={p.photoBand.blocks} prefix="band" /></div></div>
        </section>
      )}
</>);
  const renderInventory = (heading: string, cars = p.cars, searchHref?: string, state?: ShelfState) => (
      <section className="mlx-cars">
        <div className="mlx-center">
          <h2 className="mlx-title">{heading}</h2>
          {p.inventoryControls}
          {p.inventoryNotice}
           {(state?.loading ?? p.loading) ? (
            <div className="mlx-grid" aria-busy="true">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="mlx-skel" />)}</div>
           ) : (state?.unavailable ?? p.unavailable) ? (
            <div className="mlx-note" role="alert">
              <p>{p.unavailableLabel ?? t("landing.listings_unavailable", "We could not load today's listings.")}</p>
               <p style={{ marginTop: 16 }}><button type="button" className="mlx-btn" onClick={state?.onRetry ?? p.onRetry} disabled={state?.fetching ?? p.fetching}>{p.retryLabel ?? t("common.try_again")}</button></p>
            </div>
          ) : cars.length === 0 ? (
            <div className="mlx-note"><p>{p.emptyLabel ?? t("landing.no_cars_found")}</p></div>
          ) : (
            <div className="mlx-grid">{cars.map((car, i) => p.renderCard ? p.renderCard(car, i) : <MustangCard key={car.id} car={car} index={i} localized={Boolean(p.lang && p.lang !== "en")} />)}</div>
          )}
          <div className="mlx-more">
            {searchHref ? <Link href={searchHref} className="mlx-btn">{p.browseLabel ?? t("landing.browse_cars")}</Link>
              : <SearchStateLink state={p.searchState} className="mlx-btn" title={p.browseLabel ?? t("landing.browse_cars")}>{p.browseLabel ?? t("landing.browse_cars")}</SearchStateLink>}
            {p.inventoryFooter}
          </div>
        </div>
      </section>
  );
  const hostEl = (<>
      {p.hostCta && (
        <section className="mlx-host">
          <div className="mlx-copy__in">
            <Link href="/list-a-car" className="mlx-btn mlx-btn--dark">{t("landing.list_your_car")}</Link>
          </div>
        </section>
      )}
</>);
  const renderReviews = (list: MarketingReview[]) => (<>
      {list.length > 0 && (
        <section className="mlx-reviews" aria-labelledby="mlx-mk-reviews">
          <div className="mlx-center">
            <div className="mlx-reviews__head">
              <h2 id="mlx-mk-reviews">{t("nav.reviews", "Reviews")}</h2>
              <div className="mlx-arrows">
                 <button type="button" aria-label={t("common.back")} onClick={() => scroll(-1)}>&lsaquo;</button>
                 <button type="button" aria-label={t("common.next")} onClick={() => scroll(1)}>&rsaquo;</button>
              </div>
            </div>
            <div className="mlx-track" ref={track}>
              {list.map((r) => (
                <article className="mlx-rev" key={r.id}>
                   {r.carImageUrl && <div className="mlx-rev__img"><img src={r.carImageUrl} srcSet={r.carImageSrcSet} width={r.carImageWidth} height={r.carImageHeight} sizes={r.carImageSrcSet ? "(min-width:1024px) 416px,82vw" : undefined} alt={r.carName || ""} loading="lazy" decoding="async" /></div>}
                  <div className="mlx-rev__top">
                    <h3 className="mlx-rev__name">{r.reviewerName}</h3>
                    <div className="mlx-stars" role="img" aria-label={`${r.rating}/5`}>
                      {Array.from({ length: Math.round(r.rating) }).map((_, i) => <Star key={i} className="h-5 w-5" fill="currentColor" strokeWidth={0} aria-hidden="true" />)}
                    </div>
                  </div>
                  <p className="mlx-rev__txt" style={{ maxHeight: "none" }}>{r.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
</>);
  const afterEl = (<>
      {(p.afterBlocks || p.afterArt) && (
        <section className="mlx-copy mlx-copy--flat">
          <div className="mlx-copy__in">
            <Blocks blocks={p.afterBlocks} prefix="after" />
            <Links links={p.afterLinks} label={t("footer.how_to_rent")} />
          </div>
          <Art art={p.afterArt} sizes="(min-width:768px) 704px, 100vw" />
        </section>
      )}
</>);
  return (
    <div className="mustang-source-landing" data-marketing-source-landing="true" lang={p.lang} {...p.dataAttrs}>
      <section className="mlx-hero">
        {p.hero.src && <img src={p.hero.src} srcSet={p.hero.srcSet || undefined} width={p.hero.width} height={p.hero.height} style={p.hero.objectPosition ? { objectPosition: p.hero.objectPosition } : undefined} sizes={p.hero.srcSet ? "100vw" : undefined} alt={p.hero.alt} fetchPriority="high" decoding="async" />}
        <div className="mlx-center mlx-hero__in">
          <div className="mlx-hero__box">
            {p.heroTop}
            <h1>{p.heading}</h1>
            <p>{p.subtitle}</p>
            {p.heroAction ? p.heroAction : p.heroHostCta ? <Link href="/list-a-car" className="mlx-btn">{t("landing.list_your_car")}</Link>
              : <MustangLocationSearch searchState={p.searchState} localized={p.lang !== "en" && Boolean(p.lang)} />}
          </div>
        </div>
      </section>

      {p.sections ? p.sections.map((sec, i) => sec.kind === "inventory" ? <div key={i}>{sec.location && guideCityInventoryPaths[sec.location.city]
        ? <GuideCityShelf section={sec} render={renderInventory} />
        : renderInventory(sec.heading || p.inventoryHeading, locationCars(p.cars, sec.location), sec.searchHref)}</div>
        : sec.kind === "reviews" ? <div key={i}>{renderReviews(sec.reviews)}</div>
        : (
          <div key={i}>
            {sec.background && <section className="mlx-band"><img src={sec.background.src} srcSet={sec.background.srcSet} width={sec.background.width} height={sec.background.height} sizes="100vw" alt={sec.background.alt || ""} loading="lazy" decoding="async" /></section>}
            <section className={i === 0 ? "mlx-copy" : "mlx-copy mlx-copy--flat"}>
              <div className="mlx-copy__in"><Blocks blocks={sec.blocks} prefix={`s${i}`} />
                {sec.hostCta && <Link href="/list-a-car" className="mlx-btn mlx-btn--dark">{t("landing.list_your_car")}</Link>}
              </div>
              <Art art={sec.art} sizes="(min-width:768px) 704px, 100vw" />
            </section>
          </div>
        )) : (<>
        {introEl}
        {renderInventory(p.inventoryHeading)}
        {p.hostCta && hostEl}
        {renderReviews(p.reviews || [])}
        {afterEl}
      </>)}

      {(p.seoSupplement.faqs.length > 0 || p.seoSupplement.internalLinks.length > 0) && (
        <section className="mlx-seo">
          {p.seoSupplement.faqs.length > 0 && (
            <div className="mlx-copy__in">
              <h2 className="mlx-title">{p.faqHeadingLabel ?? t("landing.faq_heading")}</h2>
              <div className="mlx-faqs">
                {p.seoSupplement.faqs.map((f, i) => (
                  <details key={`${f.q}-${i}`}><summary>{f.q}</summary><p>{f.a}</p></details>
                ))}
              </div>
            </div>
          )}
          {p.seoSupplement.internalLinks.length > 0 && (
            <nav className="mlx-center" aria-label={t("landing.related_pages")}>
              <h2 className="mlx-title">{p.relatedHeadingLabel ?? t("landing.explore_more")}</h2>
              {[...new Set(p.seoSupplement.internalLinks.map((l) => l.section))].map((s) => (
                <div className="mlx-seo-group" key={s}>
                  <h3>{s}</h3>
                  <ul className="mlx-seo-links">
                    {p.seoSupplement.internalLinks.filter((l) => l.section === s).map((l) => (
                      <li key={l.href}><Link href={l.href}>{l.label}<span aria-hidden="true"> &rarr;</span></Link></li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          )}
        </section>
      )}
    </div>
  );
}
